import { isAfter, isBefore } from "date-fns";
import { startCase } from "lodash-es";
import zod from "zod";

import { ValidationIssue } from "../schema/graphql.js";

export const zodDateString = zod.custom<string>((val) => {
  return /$({\d+}-${\d+}-${\d+})|(\d{4}-\d{2}-\d{2})/.test(val as string);
}, "Invalid date");

export const zodDateStringStartingYear = zod.custom<string>((val) => {
  return /^\d{4}-\d{2}-\d{2}$/.test(val as string);
}, "Invalid date");

export const zodPastDate = zod.custom<string>((val) => {
  return !isAfter(new Date(val as string), new Date());
}, "Date must be in the past");

export const zodFutureDate = zod.custom<string>((val) => {
  return !isBefore(new Date(val as string), new Date());
}, "Date must be in the future");

export enum CommonValidationErrMsgs {
  REQUIRED = "This field cannot be empty.",
  NO_SPECIAL_CHARS = "Please avoid using special characters.",
  EMAIL = "Please enter a valid email address.",
  PHONE_NUMBER = "Please provide a valid phone number.",
}

interface DbRecord extends Record<string, unknown> {
  id: number;
}

function entityIsDbRecord(entity: unknown | DbRecord): entity is DbRecord {
  return (entity as DbRecord).id !== undefined;
}

export function fieldErrsFromIssues(issues: ValidationIssue[]) {
  const errors = {} as { [key: string]: string };
  for (const issue of issues) {
    if (issue.field) {
      const msg = issue.errors
        .map(
          (e) =>
            `${
              e.path
                ? `${startCase(e.path?.join(" ").replaceAll("_", " "))}: `
                : ""
            } ${e.message}`,
        )
        .join(" ");
      errors[issue.field] = msg;
    }
  }
  return errors;
}

export function validate(
  entity: unknown,
  schema: zod.Schema,
): ValidationIssue[] {
  const result = schema.safeParse(entity);
  if (result.success) {
    return [];
  }
  // just get what we care about
  const errors = result.error.flatten((issue: zod.ZodIssue) => {
    return {
      message: issue.message,
      errorCode: issue.code,
      path: issue.path,
    };
  });
  // field specific errors come as a key value obj so we map this to a ValidationIssue[]
  const fieldErrors = Object.keys(errors.fieldErrors).map((key: string) => {
    return {
      field: key,
      entityId: entityIsDbRecord(entity) ? entity.id : undefined,
      errors:
        errors.fieldErrors[key]?.map((err) => {
          return {
            message: err.message,
            path: err.path.map((val) => val.toString()),
            code: err.errorCode,
          };
        }) ?? [],
    };
  });
  // schema wide errors are a simple array
  const formErrors = errors.formErrors.map((err) => {
    return {
      field: undefined,
      entityId: entityIsDbRecord(entity) ? entity.id : undefined,
      errors: [
        {
          message: err.message,
          path: err.path.map((val) => val.toString()),
          code: zod.ZodIssueCode.custom,
        },
      ],
    };
  });
  // combine
  return [...fieldErrors, ...formErrors];
}
